import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "ticketino-api-client";
import { useLanguage } from "../hooks/useLanguage";
import { useOrder } from "../hooks/useOrder";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

import infoIcon from '../images/icons/info-red.svg';
import infoIconWhite from '../images/icons/info-white.svg';

// Import images for goodiebags
import wowGoodieBagImg from '../images/addons/addon-birthday-goodie-bag.png';
import wowBlackTapImg from '../images/addons/addon-birthday-blacktap.png';
import raumVisAVisImg from '../images/addons/addon-vis-a-vis.png'; // Replace with actual image path

const Addons_Birthday = () => {
    const AddOnsConfig = {
        tour: {
            quantity: 1,
            priceDE12: 110.00,
            priceEN12: 140.00,
            priceFR12: 140.00,
            priceDE24: 200.00,
            priceEN24: 230.00,
            priceFR24: 230.00,
            externalTicketCodeDE12: "bday addon tour de 12",
            externalTicketCodeEN12: "bday addon tour en 12",
            externalTicketCodeFR12: "bday addon tour fr 12",
            externalTicketCodeDE24: "bday addon tour de 24",
            externalTicketCodeEN24: "bday addon tour en 24",
            externalTicketCodeFR24: "bday addon tour fr 24",
            TicketsQuantitylimitToDisplay: 24,
        },
        visitorSupport: {
            quantity: 2,
            price: 80.00,
            externalTicketCode: "group addon host",
            TicketsQuantityNeededToDisplay: 37,
        },
        giftGoodieBag: {
            quantity: 60,
            price: 10.00,
            externalTicketCode: "bday addon goodie bag",
            image: wowGoodieBagImg,
        },
        blackTapMilkshake: {
            quantity: 1,
            image: wowBlackTapImg,
        },
        raumVisAVis: {
            TicketsQuantitylimitToDisplay: 20,
            options: [
                {
                    label: "Kurzmiete",
                    price: 150.00,
                    externalTicketCode: "bday addon raum kurzmiete"
                },
                //{
                //    label: "Halbtagesmiete",
                //    price: 260.00,
                //    externalTicketCode: "bday addon raum halbtagesmiete"
                //},
                //{
                //    label: "Ganztagesmiete",
                //    price: 450.00,
                //    externalTicketCode: "bday addon raum ganztagesmiete"
                //}
            ],
            image: raumVisAVisImg,
        },
    }

    const [bookingType, setBookingType] = useState(null);

    const [resources, setResources] = useState({});

    const [hasBlackTapMilkshake, setHasBlackTapMilkshake] = useState("");

    const [quantities, setQuantities] = useState({});

    const [selectedAddon, setSelectedAddon] = useState("");
    let [tourLanguageSelected, setTourLanguageSelected] = useState(AddOnsConfig.tour.externalTicketCodeDE);
    
    const [errorMsg, setErrorMsg] = useState("");
    
    // State for Raum Vis a Vis dropdown selection
    const [raumVisAVisSelected, setRaumVisAVisSelected] = useState("");

    const selectedTickets = JSON.parse(localStorage.getItem('selectedTickets'));

    // Calculate total booked tickets from selectedTickets
    const totalBookedTickets = selectedTickets?.tickets?.reduce((sum, t) => sum + (t.quantity || 0), 0) || 0;

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    // fetching from params
    const { language } = useLanguage();

    const order = useOrder(baseUrl);

    useEffect(() => {
        document.body.classList.remove('no-scroll');
        setBookingType(localStorage.getItem("bookingType"));
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (resources?._No && !hasBlackTapMilkshake) {
            setHasBlackTapMilkshake(resources._No);
        }
    }, [resources]);

    // Utility to get the correct config suffix based on total booked tickets
    function getSuffix(total) {
        if (total <= 12) return "12";
        if (total <= 24) return "24";
        return "24"; // fallback to 24 for >24
    }
    const suffix = getSuffix(totalBookedTickets);

    // Set default dropdown selection for language when suffix changes
    useEffect(() => {
        setTourLanguageSelected(AddOnsConfig.tour[`externalTicketCodeDE${suffix}`]);
    }, [suffix]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    // Handle quantity change
    const handleQuantityChange = (externalTicketCode, value) => {
        setQuantities(prev => ({ ...prev, [externalTicketCode]: value }));
    };

    // Handler for radio button selection
    const handleAddonChange = (value) => {
        setSelectedAddon(value);
        setErrorMsg("");
    };

    // Dropdown tour language
    const handleTourLanguageDropdown = (lang) => {
        setTourLanguageSelected(lang);
    };

    // Handler for Raum Vis a Vis dropdown
    const handleRaumVisAVisDropdown = (value) => {
        setRaumVisAVisSelected(value);
        // If Raum Vis-à-Vis is selected, reset Black Tap to "No"
        if (value && resources?._No) {
            setHasBlackTapMilkshake(resources._No);
        }
    };

    // Handler for Black Tap dropdown
    const handleBlackTapDropdown = (value) => {
        setHasBlackTapMilkshake(value);
        // If Black Tap is set to "Yes", reset Raum Vis-à-Vis
        if (value === resources?._Yes) {
            setRaumVisAVisSelected("");
        }
    };

    const handleNext = () => {
        // Check if tour options are available
        const showTourOptions = totalBookedTickets <= AddOnsConfig.tour.TicketsQuantitylimitToDisplay;

        // Only require addon selection if tour options are available and no visitor support or goodiebags selected
        const hasVisitorSupport = totalBookedTickets >= AddOnsConfig.visitorSupport.TicketsQuantityNeededToDisplay && 
                                   quantities[AddOnsConfig.visitorSupport.externalTicketCode] > 0;
        const hasGoodiebags = Object.keys(quantities).some(key => 
            key !== AddOnsConfig.visitorSupport.externalTicketCode && quantities[key] > 0
        );
        const hasRaumVisAVis = raumVisAVisSelected !== "";

        if (showTourOptions && !selectedAddon && !hasVisitorSupport && !hasGoodiebags) {
            setErrorMsg(resources?._ErrorPleaseChooseAnOption);
            return;
        }

        const selectedTickets = [];
        let selectedTourName = "";
        let selectedHostInfo = "";

        // add selected tour
        if (selectedAddon === "tour") {
            let tourQuantity = 1;

            // when 25 or more people: Automatically change it so that two tours are set in the basket
            if (totalBookedTickets >= 25) {
                tourQuantity = 2;
            }

            let externalTicketCode = tourLanguageSelected;

            selectedTickets.push({
                externalTicketCode: externalTicketCode,
                quantity: tourQuantity
            });
            
            // Determine the tour language name
            const tourLanguage = tourLanguageSelected === AddOnsConfig.tour[`externalTicketCodeEN${suffix}`]
                ? "Englisch"
                : tourLanguageSelected === AddOnsConfig.tour[`externalTicketCodeFR${suffix}`]
                    ? "Französisch"
                    : "Deutsch";
            selectedTourName = `WOW B-Day Tour (${tourLanguage})`;
        }

        // Add Raum Vis a Vis if selected
        if (raumVisAVisSelected) {
            selectedTickets.push({
                externalTicketCode: raumVisAVisSelected,
                quantity: 1
            });
        }

        Object.entries(quantities).forEach(([externalTicketCode, quantity]) => {
            if (quantity > 0) {
                selectedTickets.push({
                    externalTicketCode,
                    quantity
                });
                
                // Format host info for step60_information orderOption15
                if (externalTicketCode === AddOnsConfig.visitorSupport.externalTicketCode) {
                    selectedHostInfo = `${quantity}`;
                }
            }
        });

        //remove kombi-ticket from session (only used for group booking type)
        localStorage.removeItem("hasKombiTicket");

        // Save Black Tap Milkshake before proceeding (needed for the information page)
        // Store as "yes" or "no" based on resources translation
        localStorage.setItem("hasBlackTapMilkshake", hasBlackTapMilkshake);

        // Save selected tour name to localStorage for use in order options
        if (selectedTourName) {
            localStorage.setItem("selectedTourName", selectedTourName);
        } else {
            localStorage.removeItem("selectedTourName");
        }

        // Save selected host info to localStorage for use in order options
        if (selectedHostInfo) {
            localStorage.setItem("selectedHostInfo", selectedHostInfo);
        } else {
            localStorage.removeItem("selectedHostInfo");
        }

        // If "none" was selected and no other add-ons, remove add-ons
        if (selectedAddon === "none" && selectedTickets.length === 0) {
            localStorage.removeItem('selectedTicketsAddons');
            navigate(`/${language}/calendar`);
            return;
        }

        // If no tour options available and no add-ons selected
        if (!showTourOptions && selectedTickets.length === 0) {
            localStorage.removeItem('selectedTicketsAddons');
            navigate(`/${language}/calendar`);
            return;
        }

        // Prepare session data (overwrite all previous add-ons)
        const sessionData = {
            eventGroupId: 3553,
            tickets: selectedTickets
        };

        // Save to localStorage (replace all add-ons)
        localStorage.setItem('selectedTicketsAddons', JSON.stringify(sessionData));

        // Navigate to next page
        navigate(`/${language}/calendar`);
    };

    const canProceed = true;

    //const canProceed =
    //    otherAddOns.some(ticketType => quantities[ticketType.id] > 0) ||
    //    Object.values(dropdownGroups).some(g => g && g.checked) ||
    //    Object.values(radioGroups).some(g => g && g.dropdown);

    return (
        <>
            <Header language={language} pagePath={`addons-birthday`} pageTitle={resources?.AddOnsTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">
                <div className="container wrapper">

                    {totalBookedTickets <= AddOnsConfig.tour.TicketsQuantitylimitToDisplay && (
                        <>
                            <div className="row">
                                <p>
                                    <strong>
                                        {resources?.AddOnsDesiredGuidedTour}
                                    </strong>
                                </p>
                            </div>

                            <div className="row custom-box-with-border" style={{ marginLeft: "2px", marginRight: "2px" }}>
                                <table className="table table-no-border table-align-middle" style={{ marginBottom: 0 }}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                    <input
                                                        type="radio"
                                                        name="addon"
                                                        value="tour"
                                                        checked={selectedAddon === "tour"}
                                                        onChange={() => handleAddonChange("tour")}
                                                    />
                                                    <span dangerouslySetInnerHTML={{ __html: resources?.AddOnsBirthdayGuidedTour }}></span>
                                                </label>
                                            </td>
                                            <td style={{ minWidth: "120px", textAlign: "right" }}>
                                                {tourLanguageSelected === AddOnsConfig.tour[`externalTicketCodeEN${suffix}`]
                                                    ? `CHF ${AddOnsConfig.tour[`priceEN${suffix}`].toFixed(2)}`
                                                    : tourLanguageSelected === AddOnsConfig.tour[`externalTicketCodeFR${suffix}`]
                                                        ? `CHF ${AddOnsConfig.tour[`priceFR${suffix}`].toFixed(2)}`
                                                        : `CHF ${AddOnsConfig.tour[`priceDE${suffix}`].toFixed(2)}`
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2">
                                                <div style={{ marginLeft: 30 }}>
                                                    <span >{resources?.AddOnsBirthdayGuidedTourDesc}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={3}>
                                                <div style={{ marginLeft: 30, marginTop: 2, marginBottom: 2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span>{resources?.AddOnsLanguage}:</span>
                                                    <select
                                                        value={tourLanguageSelected}
                                                        onChange={e => handleTourLanguageDropdown(e.target.value)}
                                                        style={{ minWidth: 100, flexShrink: 0 }}
                                                        className="form-select language-select"
                                                        disabled={selectedAddon !== "tour"}
                                                    >
                                                        <option value={AddOnsConfig.tour[`externalTicketCodeDE${suffix}`]}>Deutsch</option>
                                                        <option value={AddOnsConfig.tour[`externalTicketCodeEN${suffix}`]}>Englisch</option>
                                                        <option value={AddOnsConfig.tour[`externalTicketCodeFR${suffix}`]}>Französisch</option>
                                                    </select>
                                                    <div className="info-icon-wrapper">
                                                        <img src={infoIcon} alt="Info" className="info-icon" />
                                                        <span className="tooltip">
                                                            {resources?.AddOnsBirthdayGuidedTourToolTip}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* No tour */}
                            <table className="mt-4 ms-3 table table-no-border table-align-middle" style={{ marginBottom: 0 }}>
                                <tbody>
                                    {/* No tour radiobutton */}
                                    <tr>
                                        <td>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <input
                                                    type="radio"
                                                    name="addon"
                                                    value="none"
                                                    checked={selectedAddon === "none"}
                                                    onChange={() => handleAddonChange("none")}
                                                />
                                                <span>
                                                    {resources?.AddOnsNoTour}
                                                </span>
                                            </label>
                                        </td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Error message */}
                            {errorMsg && (
                                <div className="custom-box text-red mt-3">{errorMsg}</div>
                            )}
                        </>
                    )}

                    {/* host */}
                    {totalBookedTickets >= AddOnsConfig.visitorSupport.TicketsQuantityNeededToDisplay && (
                        <>
                            <div className="row mt-4">
                                <p><strong>{resources?.AddOnsHost}</strong></p>
                            </div>

                            <div className="row custom-box-with-border" style={{ marginLeft: "2px", marginRight: "2px" }}>
                                <table className="table table-no-border table-align-middle" style={{ marginBottom: 0, width: "100%" }}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <label className="ps-4" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                    <strong>
                                                        <span>{resources?.AddOnsHostGroup}</span>
                                                    </strong>
                                                    <div className="info-icon-wrapper">
                                                        <img src={infoIcon} alt="Info" className="info-icon" style={{ marginLeft: 0 }} />
                                                        <span className="tooltip">
                                                            {resources?.AddOnsHostGroupToolTip}
                                                        </span>
                                                    </div>
                                                </label>
                                            </td>
                                            <td colSpan={2} style={{ width: "1%", paddingRight: "0" }}>
                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    alignItems: "center",
                                                    gap: "2rem"
                                                }}>
                                                    <span style={{ minWidth: "120px", textAlign: "right" }}>
                                                        CHF {AddOnsConfig.visitorSupport.price.toFixed(2)}
                                                    </span>
                                                    
                                                    <select
                                                        className="form-select qtd-select"
                                                        value={quantities[AddOnsConfig.visitorSupport.externalTicketCode] || 0}
                                                        onChange={e => handleQuantityChange(AddOnsConfig.visitorSupport.externalTicketCode, Number(e.target.value))}
                                                    >
                                                        {[...Array(AddOnsConfig.visitorSupport.quantity + 1).keys()].map(qty => (
                                                            <option key={qty} value={qty}>{qty}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="row mt-4 ps-3 text-center">
                                    <p dangerouslySetInnerHTML={{ __html: resources?.AddOnsHostGroupDesc }}></p>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="row">
                        <div className="col-md-12">
                            <hr className="ticket-separator" />
                        </div>
                    </div>

                    <div className="row mt-2">
                        <p><strong>Specials</strong></p>
                    </div>

                    <table className="table table-no-border table-align-middle" style={{ marginBottom: 0, width: "100%" }}>
                        <tbody>
                            {/* WOW Goodie bag */}
                            <tr>
                                <td colSpan={2} style={{ padding: 0, border: 'none' }}>
                                    <div className="goodie-card">
                                        <div className="goodie-card-image">
                                            <img src={AddOnsConfig.giftGoodieBag.image} alt="Goodie bag" />
                                        </div>
                                        <div className="goodie-card-content">
                                            <div className="fw-bold">{resources?.AddOnsGoodieBag}</div>
                                            <div>{resources?.AddOnsGoodieBagDesc}</div>
                                            <div className="mt-3">CHF {AddOnsConfig.giftGoodieBag.price.toFixed(2)}</div>
                                            <div className="goodie-select mt-3">
                                                <select
                                                    className="form-select qtd-select"
                                                    style={{ minWidth: 120, backgroundColor: "#fff" }}
                                                    onChange={e => handleQuantityChange(AddOnsConfig.giftGoodieBag.externalTicketCode, Number(e.target.value))}
                                                >
                                                    {[...Array(AddOnsConfig.giftGoodieBag.quantity + 1).keys()].map(qty => (
                                                        <option key={qty} value={qty}>{qty}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            {/* Black Tap Milkshake reservation */}
                            <tr>
                                <td colSpan={2} style={{ padding: 0, border: 'none' }}>
                                    <div className="goodie-card">
                                        <div className="goodie-card-image">
                                            <img src={AddOnsConfig.blackTapMilkshake.image} alt="Black tap reservation" />
                                        </div>
                                        <div className="goodie-card-content">
                                            <div className="fw-bold">{resources?.AddOnsGiftBlackTap}</div>
                                            <div>{resources?.AddOnsGiftBlackTapDesc}</div>
                                            <div className="goodie-select mt-3">
                                                <select
                                                    className="form-select qtd-select"
                                                    style={{ minWidth: 120, backgroundColor: "#fff" }}
                                                    value={hasBlackTapMilkshake}
                                                    onChange={e => handleBlackTapDropdown(e.target.value)}
                                                    disabled={raumVisAVisSelected !== ""}
                                                >
                                                    <option value={resources?._No}>{resources?._No}</option>
                                                    <option value={resources?._Yes}>{resources?._Yes}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            {/* Raum vis a vis */}
                            {totalBookedTickets <= AddOnsConfig.raumVisAVis.TicketsQuantitylimitToDisplay && (
                                <tr>
                                    <td colSpan={2} style={{ padding: 0, border: 'none' }}>
                                        <div className="goodie-card">
                                            <div className="goodie-card-image">
                                                <img src={AddOnsConfig.raumVisAVis.image} alt="Raum vis a vis" />
                                            </div>
                                            <div className="goodie-card-content">
                                                <div className="fw-bold">{resources?.AddOnsGiftRaumVisAVis}</div>
                                                <div className="ps-md-4 pe-md-4 text-center" dangerouslySetInnerHTML={{ __html: resources?.AddOnsGiftRaumVisAVisDesc }}></div>
                                                <div className="mt-3">
                                                    {raumVisAVisSelected && (
                                                        <span>
                                                            CHF {AddOnsConfig.raumVisAVis.options.find(opt => opt.externalTicketCode === raumVisAVisSelected)?.price.toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="goodie-select mt-3">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <select
                                                            className="ms-md-4 form-select qtd-select"
                                                            style={{ minWidth: 120, backgroundColor: "#fff" }}
                                                            value={raumVisAVisSelected}
                                                            onChange={e => handleRaumVisAVisDropdown(e.target.value)}
                                                            disabled={hasBlackTapMilkshake === resources?._Yes}
                                                        >
                                                            <option value=""></option>
                                                            {AddOnsConfig.raumVisAVis.options.map(option => (
                                                                <option key={option.externalTicketCode} value={option.externalTicketCode}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <div className="info-icon-wrapper">
                                                            <img src={infoIconWhite} alt="Info" className="info-icon" style={{ marginLeft: 0 }} />
                                                            <span className="tooltip">
                                                                {resources?.AddOnsRaumVisAVisToolTip}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="row mt-5">
                        <div className="col-6">
                            <button
                                className="btn-custom white"
                                type="submit"
                                onClick={() => navigate(-1)}
                            >
                                {resources?._Back}
                            </button>
                        </div>

                        <div className="col-6 text-end">
                            <button
                                className="btn-custom"
                                type="submit"
                                onClick={handleNext}
                                disabled={!canProceed}
                            >
                                {resources?._Next}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Footer language={language} />
        </>
    )
}

export default Addons_Birthday